import { cx } from 'utils';

/**
 * PageHeader — consistent dashboard / page title block.
 *
 * Pairs a display title with an optional eyebrow, description, breadcrumb slot,
 * and a trailing actions area. Drop it at the top of any page for instant
 * visual consistency.
 *
 * @param {string} title
 * @param {string} [eyebrow]      Small label above the title (e.g. section).
 * @param {string} [description]
 * @param {node}   [actions]      Buttons / controls aligned to the right.
 * @param {node}   [breadcrumb]   Optional breadcrumb element above the eyebrow.
 * @param {node}   [icon]         Optional leading icon tile.
 * @param {boolean} [border=true] Show the bottom hairline divider.
 */
export default function PageHeader({
  title,
  eyebrow,
  description,
  actions,
  breadcrumb,
  icon,
  border = true,
  className = '',
  ...props
}) {
  return (
    <div
      className={cx(
        'flex flex-col gap-4 pb-6 sm:flex-row sm:items-end sm:justify-between',
        border && 'border-b border-slate-100',
        className
      )}
      {...props}
    >
      <div className="min-w-0">
        {breadcrumb && <div className="mb-2">{breadcrumb}</div>}

        <div className="flex items-center gap-3.5">
          {icon && (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100">
              {icon}
            </span>
          )}
          <div className="min-w-0">
            {eyebrow && (
              <p className="lx-eyebrow mb-0.5">
                {eyebrow}
              </p>
            )}
            <h1 className="truncate font-display text-display-xs font-semibold text-slate-900 sm:text-display-sm">
              {title}
            </h1>
          </div>
        </div>

        {description && (
          <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-slate-500">{description}</p>
        )}
      </div>

      {actions && <div className="flex shrink-0 items-center gap-3">{actions}</div>}
    </div>
  );
}
